import React, { useEffect, useState } from 'react';
import platformApi, { platformErrorMessage } from '../services/platformApi';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import GoLiveChecklist from '../components/GoLiveChecklist';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';

const EMPTY = {
  legalName: '',
  tradeName: '',
  gstin: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateCode: '',
  postalCode: '',
  email: '',
  phone: '',
  invoicePrefix: '',
  sac: '998314',
};

function fromSettings(settings) {
  if (!settings) return { ...EMPTY };
  return Object.fromEntries(Object.keys(EMPTY).map((key) => [key, settings[key] ?? EMPTY[key]]));
}

function BillingSettings() {
  const toast = useToast();
  const confirm = useConfirm();
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(null);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    Promise.all([
      platformApi.get('/platform/settings'),
      platformApi.get('/platform/gst-states'),
    ])
      .then(([settings, gstStates]) => {
        const current = settings.data?.data || null;
        setSaved(current);
        setForm(fromSettings(current));
        setStates(gstStates.data?.data || []);
      })
      .catch((error) => setLoadError(platformErrorMessage(error, 'Failed to load billing settings')))
      .finally(() => setLoading(false));
  }, []);

  const set = (key) => (e) => {
    const value = key === 'gstin' || key === 'invoicePrefix' ? e.target.value.toUpperCase() : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saved?.invoicePrefix && saved.invoicePrefix !== form.invoicePrefix) {
      const ok = await confirm({
        title: 'Change invoice prefix?',
        message: 'The prefix can only change before any invoice uses it. Invoice numbers restart under the new prefix.',
        confirmLabel: 'Change prefix',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const response = await platformApi.put('/platform/settings', form);
      const current = response.data?.data || null;
      setSaved(current);
      setForm(fromSettings(current));
      setErrors({});
      toast.success('Seller details saved. New invoices will use them.');
    } catch (error) {
      const fieldErrors = error.response?.data?.details?.errors;
      if (Array.isArray(fieldErrors)) {
        setErrors(Object.fromEntries(fieldErrors.map((item) => [item.field, item.message])));
      }
      toast.error(platformErrorMessage(error, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingScreen message="Loading billing settings..." />;

  const field = (key, label, props = {}) => (
    <div className="form-group">
      <label className="form-label" htmlFor={`seller-${key}`}>{label}</label>
      <input id={`seller-${key}`} className="form-input" value={form[key]} onChange={set(key)} {...props} />
      {errors[key] && <p className="form-hint" style={{ color: 'var(--color-danger-text)' }}>{errors[key]}</p>}
    </div>
  );

  return (
    <div className="page">
      <PageHeader
        title="Billing settings"
        description="What's left before taking live payments, and your company's GST details printed on every tax invoice and credit note."
      />
      <GoLiveChecklist refreshKey={saved?.updatedAt} />
      {loadError && <div className="alert alert--error">{loadError}</div>}
      {!saved && !loadError && (
        <div className="alert alert--error">
          Seller details are not saved yet, so payments currently get no tax invoice.
        </div>
      )}
      <form className="card card__body" onSubmit={submit}>
        <div className="form-grid">
          {field('legalName', 'Legal name', { required: true, maxLength: 200 })}
          {field('tradeName', 'Trade name (optional)', { maxLength: 200 })}
          {field('gstin', 'GSTIN', { required: true, maxLength: 15, placeholder: '33ABCDE1234F1Z5' })}
          {field('invoicePrefix', 'Invoice prefix', { required: true, maxLength: 4, placeholder: 'INY' })}
          {field('addressLine1', 'Address line 1', { required: true })}
          {field('addressLine2', 'Address line 2 (optional)')}
          {field('city', 'City', { required: true })}
          <div className="form-group">
            <label className="form-label" htmlFor="seller-stateCode">State</label>
            <select id="seller-stateCode" className="form-select" value={form.stateCode} onChange={set('stateCode')} required>
              <option value="">Choose a state</option>
              {states.map((s) => <option key={s.code} value={s.code}>{s.code} — {s.name}</option>)}
            </select>
            {errors.stateCode && <p className="form-hint" style={{ color: 'var(--color-danger-text)' }}>{errors.stateCode}</p>}
          </div>
          {field('postalCode', 'PIN code', { required: true, inputMode: 'numeric', maxLength: 6 })}
          {field('email', 'Billing email (optional)', { type: 'email' })}
          {field('phone', 'Phone (optional)', { maxLength: 20 })}
          {field('sac', 'SAC code', { maxLength: 6 })}
        </div>
        <p className="form-hint">
          The GSTIN's state must match the address state. Customers in the same state are charged CGST + SGST; others IGST.
          {saved?.updatedAt ? ` Last saved ${new Date(saved.updatedAt).toLocaleString('en-IN')}.` : ''}
        </p>
        <div className="btn-group" style={{ justifyContent: 'flex-end' }}>
          <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save seller details'}</button>
        </div>
      </form>
    </div>
  );
}

export default BillingSettings;
